import apiClient from './api';

class ComplianceService {
  // ─── Licenses ───────────────────────────────────────────────────────────────
  async getLicenses() {
    try {
      const response = await apiClient.get('/compliance/licenses');
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch licenses.';
    }
  }

  async getLicense(id) {
    try {
      const response = await apiClient.get(`/compliance/licenses/${id}`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch license.';
    }
  }

  async createLicense(dto) {
    try {
      const response = await apiClient.post('/compliance/licenses', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create license.';
    }
  }

  async updateLicense(id, dto) {
    try {
      const response = await apiClient.put(`/compliance/licenses/${id}`, dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update license.';
    }
  }

  async deleteLicense(id) {
    try {
      await apiClient.delete(`/compliance/licenses/${id}`);
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete license.';
    }
  }

  async getExpiringLicenses(days = 30) {
    try {
      const response = await apiClient.get('/compliance/licenses/expiring-soon', { params: { days } });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch expiring licenses.';
    }
  }

  // ─── Permits ────────────────────────────────────────────────────────────────
  async getPermits() {
    try {
      const response = await apiClient.get('/compliance/permits');
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch permits.';
    }
  }

  async getPermit(id) {
    try {
      const response = await apiClient.get(`/compliance/permits/${id}`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch permit.';
    }
  }

  async createPermit(dto) {
    try {
      const response = await apiClient.post('/compliance/permits', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create permit.';
    }
  }

  async updatePermit(id, dto) {
    try {
      const response = await apiClient.put(`/compliance/permits/${id}`, dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update permit.';
    }
  }

  async deletePermit(id) {
    try {
      await apiClient.delete(`/compliance/permits/${id}`);
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete permit.';
    }
  }

  async getExpiringPermits(days = 30) {
    try {
      const response = await apiClient.get('/compliance/permits/expiring-soon', { params: { days } });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch expiring permits.';
    }
  }

  // ─── Products ───────────────────────────────────────────────────────────────
  // Normalize API product shape to the field names used by the frontend
  _normalizeProduct(p) {
    return {
      ...p,
      id: p.productId ?? p.id,
      name: p.productName ?? p.name,
      categoryId: p.productCategoryId ?? p.categoryId,
      strain: p.strain ?? null,
      thcContent: p.thcContent ?? null,
      cbdContent: p.cbdContent ?? null,
      quantity: p.quantity ?? null,
      unit: p.unit ?? null,
      batchNumber: p.batchNumber ?? null,
      minQuantity: p.minQuantity ?? null,
      description: p.description ?? null,
      status: p.status ?? null,
    };
  }

  // Map frontend field names back to the API DTO field names
  _toProductPayload(dto) {
    return {
      productName: dto.name || dto.productName || '',
      productCategoryId: dto.categoryId ? parseInt(dto.categoryId, 10) : (dto.productCategoryId ?? null),
      brandName: dto.brandName || null,
      sku: dto.sku || null,
      manufacturer: dto.manufacturer || null,
      strain: dto.strain || null,
      thcContent: dto.thcContent || null,
      cbdContent: dto.cbdContent || null,
      quantity: dto.quantity != null && dto.quantity !== '' ? Number(dto.quantity) : null,
      unit: dto.unit || null,
      batchNumber: dto.batchNumber || null,
      minQuantity: dto.minQuantity != null && dto.minQuantity !== '' ? Number(dto.minQuantity) : null,
      description: dto.description || null,
      status: dto.status || null,
      isActive: dto.isActive ?? true,
    };
  }

  async getProducts() {
    try {
      const response = await apiClient.get('/compliance/products');
      const data = response.data.data ?? response.data;
      return Array.isArray(data) ? data.map(p => this._normalizeProduct(p)) : data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch products.';
    }
  }

  async getProduct(id) {
    try {
      const response = await apiClient.get(`/compliance/products/${id}`);
      const data = response.data.data ?? response.data;
      return data ? this._normalizeProduct(data) : data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch product.';
    }
  }

  async createProduct(dto) {
    try {
      const response = await apiClient.post('/compliance/products', this._toProductPayload(dto));
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create product.';
    }
  }

  async updateProduct(id, dto) {
    try {
      const response = await apiClient.put(`/compliance/products/${id}`, this._toProductPayload(dto));
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update product.';
    }
  }

  async deleteProduct(id) {
    try {
      await apiClient.delete(`/compliance/products/${id}`);
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete product.';
    }
  }

  // ─── Product Categories ─────────────────────────────────────────────────────
  async getProductCategories() {
    try {
      const response = await apiClient.get('/compliance/product-categories');
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch product categories.';
    }
  }

  async createProductCategory(dto) {
    try {
      const response = await apiClient.post('/compliance/product-categories', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create product category.';
    }
  }

  // ─── Lab Tests ──────────────────────────────────────────────────────────────
  async getLabTests(batchId) {
    try {
      const response = await apiClient.get(`/compliance/labtests/batch/${batchId}`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch lab tests.';
    }
  }

  async createLabTest(dto) {
    try {
      const response = await apiClient.post('/compliance/labtests', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create lab test.';
    }
  }

  async updateLabTest(id, dto) {
    try {
      const response = await apiClient.put(`/compliance/labtests/${id}`, dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update lab test.';
    }
  }

  async deleteLabTest(id) {
    try {
      await apiClient.delete(`/compliance/labtests/${id}`);
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete lab test.';
    }
  }

  // ─── Batches ─────────────────────────────────────────────────────────────────
  async getBatches(productId) {
    try {
      const response = await apiClient.get(`/compliance/batches/product/${productId}`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch batches.';
    }
  }

  async createBatch(dto) {
    try {
      const response = await apiClient.post('/compliance/batches', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create batch.';
    }
  }

  async updateBatch(id, dto) {
    try {
      const response = await apiClient.put(`/compliance/batches/${id}`, dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update batch.';
    }
  }

  async deleteBatch(id) {
    try {
      await apiClient.delete(`/compliance/batches/${id}`);
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete batch.';
    }
  }

  // ─── Inventory Movement ─────────────────────────────────────────────────────
  async logInventoryMovement(dto) {
    try {
      const response = await apiClient.post('/compliance/inventory/movement', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to log inventory movement.';
    }
  }

  async getInventoryByBatch(batchId) {
    try {
      const response = await apiClient.get(`/compliance/inventory/batch/${batchId}`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch inventory movements.';
    }
  }

  async getInventoryBatchSummary(batchId) {
    try {
      const response = await apiClient.get(`/compliance/inventory/batch/${batchId}/summary`);
      return response.data.data ?? response.data;
    } catch (error) {
      return null; // batch may have no inventory record yet
    }
  }

  async deleteInventoryMovement(id) {
    try {
      await apiClient.delete(`/compliance/inventory/movement/${id}`);
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete inventory movement.';
    }
  }

  // ─── Audits ──────────────────────────────────────────────────────────────────
  async getAudits(productId) {
    try {
      const response = await apiClient.get(`/compliance/audits/product/${productId}`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch audits.';
    }
  }

  async createAudit(dto) {
    try {
      const response = await apiClient.post('/compliance/audits', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create audit.';
    }
  }

  // ─── Dashboard ───────────────────────────────────────────────────────────────
  async getComplianceDashboard() {
    try {
      const response = await apiClient.get('/compliance/dashboard');
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch compliance dashboard.';
    }
  }

  async getDashboard() {
    try {
      const response = await apiClient.get('/dashboard');
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch dashboard.';
    }
  }

  // ─── Documents ───────────────────────────────────────────────────────────────
  async uploadDocument(dto) {
    try {
      const response = await apiClient.post('/compliance/documents', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to upload document.';
    }
  }
}

export default new ComplianceService();
